import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, defaultIfEmpty, from, lastValueFrom, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { OAuthEvent } from 'angular-oauth2-oidc/events';
import { AuthenticationService } from './authentication.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface User {
    email: string;
    name?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthCoreService {
    private readonly destroyRef = inject(DestroyRef);
    private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

    private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
    public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

    user$ = new BehaviorSubject<User | null>(null);

    private handleUserLogout(): void {
        this.router.navigateByUrl('/');
    }

    constructor(
        private oauthService: OAuthService,
        private authenticationService: AuthenticationService,
        private router: Router,
    ) {
        window.addEventListener('storage', (event) => {
            if (event.key !== 'access_token' && event.key != null) {
                return;
            }

            console.warn('Noticed changes to access_token (most likely from another tab)');
            this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

            if (!this.oauthService.hasValidAccessToken()) {
                this.handleUserLogout();
            }
        });

        this.oauthService.events.subscribe(() => {
            this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
        });
        this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

        this.oauthService.events
            .pipe(filter((e) => ['token_received'].includes(e.type)))
            .subscribe(() => this.oauthService.loadUserProfile());

        this.oauthService.events
            .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
            .subscribe(() => this.handleUserLogout());

        this.oauthService.setupAutomaticSilentRefresh();
    }

    public async runInitialLoginSequence(): Promise<void> {
        try {
            // Step 1: Load Keycloak discovery document
            await this.oauthService.loadDiscoveryDocument();

            // Step 2: Attempt to log in using stored session/token
            await this.oauthService.tryLogin();

            // Step 3: If token is not valid, attempt a silent refresh
            if (!this.oauthService.hasValidAccessToken()) {
                try {
                    await this.oauthService.refreshToken();
                } catch (error) {
                    console.error('Token refresh failed:', error);
                    this.login();
                    return;
                }
            }

            // Step 4: Set user information after successful authentication
            this.user$.next(this.getUser());
            this.isDoneLoadingSubject$.next(true);

            // Step 5: Wait for the authentication profile to be fully fetched
            await lastValueFrom(
                this.authenticationService.fetchAndSetProfile().pipe(defaultIfEmpty(null), takeUntilDestroyed(this.destroyRef)),
            );

            // Step 6: Navigate only when the app is fully initialized
            if (this.oauthService.state && this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
                const stateUrl = decodeURIComponent(this.oauthService.state);
                await this.router.navigateByUrl(stateUrl);
            }
        } catch (error) {
            console.error('Authentication failed:', error);
            this.isDoneLoadingSubject$.next(true);
        }
    }

    public login(targetUrl?: string): void {
        this.oauthService.initLoginFlow(targetUrl ?? this.router.url);
    }

    public logout(): void {
        this.oauthService.logOut();
    }
    public refresh(): Observable<OAuthEvent> {
        return from(this.oauthService.silentRefresh());
    }
    public hasValidToken(): boolean {
        return this.oauthService.hasValidAccessToken();
    }
    private getUser(): User {
        return this.oauthService.getIdentityClaims() as User;
    }
}
